import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../../components/Toast';
import { LabelSelect } from '../../../components/Input/Input';
import { loadCostCenters, saveCostCenter, deleteCostCenter } from '../../../core/utils/costCenter';
import { STATUS_OPTIONS, STATUS } from '../../../core/constants/financial';
import FinancialListModal from './FinancialListModal';
import CostCenterSummary from './CostCenterSummary';
import CostCenterTable from './CostCenterTable';
import CostCenterFormModal from './CostCenterFormModal';

const FILTER_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  ...STATUS_OPTIONS,
];

const INITIAL_FORM = {
  code: '',
  name: '',
  type: 'produtivo',
  responsible: '',
  percentage: '',
  status: 'ativo',
  description: '',
};

const CostCenterModal = ({ isOpen, onClose }) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});
  const [costCenters, setCostCenters] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const fetchCostCenters = async () => {
    const data = await loadCostCenters(company.id);
    setCostCenters(data);
  };

  useEffect(() => {
    if (isOpen && company.id) fetchCostCenters();
  }, [isOpen, company.id]);

  const filtered = costCenters.filter((cc) => {
    const term = search.toLowerCase();
    const matchesSearch = !term ||
      cc.code?.toLowerCase().includes(term) ||
      cc.name?.toLowerCase().includes(term);
    const matchesStatus = !filterStatus || cc.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalRateio = costCenters
    .filter((cc) => cc.status === STATUS.ATIVO)
    .reduce((sum, cc) => sum + (Number(cc.percentage) || 0), 0);

  const openForm = (cc = null) => {
    if (cc) {
      setForm({
        code: cc.code || '',
        name: cc.name || '',
        type: cc.type || 'produtivo',
        responsible: cc.responsible || '',
        percentage: cc.percentage != null ? String(cc.percentage) : '',
        status: cc.status || 'ativo',
        description: cc.description || '',
      });
      setEditingId(cc.id);
    } else {
      setForm(INITIAL_FORM);
      setEditingId(null);
    }
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(INITIAL_FORM);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (saving) return;

    if (!form.code.trim()) {
      alertError('Código é obrigatório.');
      return;
    }
    if (!form.name.trim()) {
      alertError('Nome é obrigatório.');
      return;
    }

    const pct = Number(form.percentage);
    if (pct < 0 || pct > 100) {
      alertError('Percentual de rateio deve ser entre 0 e 100.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        percentage: Number(form.percentage) || 0,
        company_id: company.id,
        created_by: user.id,
      };

      const { error } = await saveCostCenter(editingId, payload);
      if (error) {
        alertError('Erro ao salvar centro de custo.');
        return;
      }

      alertSuccess(editingId ? 'Centro de custo atualizado!' : 'Centro de custo cadastrado!');
      closeForm();
      fetchCostCenters();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cc, onSuccess) => {
    const result = await Swal.fire({
      title: 'Deseja excluir este centro de custo?',
      text: `${cc.code} - ${cc.name}`,
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (result.isDenied) {
      const { error } = await deleteCostCenter(cc.id, user.id);
      if (!error) {
        alertSuccess('Centro de custo removido!');
        fetchCostCenters();
        if (onSuccess) onSuccess();
      } else {
        alertError('Erro ao remover centro de custo.');
      }
    }
  };

  const filterSelect = (
    <div className="fd-modal-filter">
      <LabelSelect menuPortalTarget={document.body}
        options={FILTER_STATUS_OPTIONS}
        value={FILTER_STATUS_OPTIONS.find((o) => o.value === filterStatus)}
        placeholder="Todos"
        onChange={(opt) => setFilterStatus(opt?.value || '')}
      />
    </div>
  );

  return (
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title="Centro de Custo"
      badge={`${costCenters.length} cadastrados`}
      searchPlaceholder="Buscar centro de custo..."
      search={search}
      onSearchChange={setSearch}
      onNew={() => openForm()}
      newLabel="+ Novo"
      headerExtra={filterSelect}
    >
      <CostCenterSummary costCenters={costCenters} />

      <CostCenterTable
        costCenters={filtered}
        onEdit={openForm}
        totalRateio={totalRateio}
      />

      {formOpen && (
        <CostCenterFormModal
          form={form}
          setForm={setForm}
          editingId={editingId}
          onSave={handleSave}
          onClose={closeForm}
          onDelete={editingId ? () => {
            const cc = costCenters.find((c) => c.id === editingId);
            if (cc) handleDelete(cc, closeForm);
          } : undefined}
          saving={saving}
        />
      )}
    </FinancialListModal>
  );
};

export default CostCenterModal;
